import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useCatalog } from "@/lib/catalog";
import { PageHead, CardSkeleton, ErrorState, EmptyState } from "@/components/Ui";
import { Reveal } from "@/components/Reveal";
import { DynIcon } from "@/components/DynIcon";
import { ArrowRight } from "lucide-react";

export default function Sectors() {
  const { t, lang } = useLang();
  const { catalog, loading, error, reload } = useCatalog();
  const sectors = catalog?.sectors || [];
  const modules = catalog?.modules || [];
  const moduleByKey = Object.fromEntries(modules.map((m) => [m.key, m]));

  return (
    <div className="bg-grid" data-testid="sectors-page">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <PageHead eyebrow={t("sectors.eyebrow")} title={t("sectors.title")} sub={t("sectors.sub")} testid="sectors-head" />
        <div className="mt-12">
          {loading ? (
            <CardSkeleton count={8} />
          ) : error ? (
            <ErrorState onRetry={reload} />
          ) : sectors.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="sectors-grid">
              {sectors.map((s, i) => {
                const recommended = (s.recommended_modules || []).map((k) => moduleByKey[k]).filter(Boolean);
                return (
                  <Reveal key={s.key} delay={(i % 3) * 80}>
                    <div className="mg-card flex h-full flex-col p-6" data-testid={`sector-card-${s.key}`}>
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-marine text-gold">
                        <DynIcon name={s.icon} className="h-6 w-6" />
                      </div>
                      <h3 className="mt-4 font-display text-lg font-bold text-marine">{lang === "fr" ? s.name_fr : s.name_en}</h3>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-gold">
                        {recommended.length} {t("sectors.recommended")}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {recommended.slice(0, 5).map((m) => (
                          <span key={m.key} className="rounded-full border border-slate-200 bg-mist px-2.5 py-1 text-xs font-medium text-slate-600">
                            {lang === "fr" ? m.name_fr : m.name_en}
                          </span>
                        ))}
                        {recommended.length > 5 && (
                          <span className="rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">
                            +{recommended.length - 5}
                          </span>
                        )}
                      </div>
                      <Link to="/demo" className="mt-5 inline-flex items-center gap-1.5 pt-1 text-sm font-semibold text-brand hover:text-brand-dark" data-testid={`sector-cta-${s.key}`}>
                        {t("home.ctaDemo")} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
